import { IDeviationCounts } from 'src/generated-types'
import { EquipmentRequestDto } from 'src/generated-types/equipment-request-dto'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { IROVVideosWithSharedCategories } from 'src/generated-types/rovvideo'
import { mainComponentQueryKeys } from 'src/query-keys'
import { Entity } from '../generated-types/entity'
import { IExportRequest } from '../generated-types/excel-export-request'
import {
  IDiscardEquipmentDto,
  IEquipmentWithLocalityFilterDto,
  IMainComponent,
  IMainComponentSendSchema,
  IMainComponentUpdate,
  MainComponentFilters,
} from '../generated-types/main-component'
import { IMainComponentStatusCount } from '../generated-types/main-component-status-count'
import { MainComponentsRequestDto } from '../generated-types/main-components-request-dto'
import { queryClient } from '../query-client'
import { invalidationActivityLogCache } from './activity-log'
import { apiConsts } from './apiConsts'
import { invalidateLocalityCache } from './localities'
import { IPaginatedResult } from './paginated-result'
import { invalidatePositionCache } from './positions'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { rovVideosRefetchInterval } from './rov-videos'

const mainComponentBaseURL = (customerId: EntityId) => `customers/${customerId}/main-components`

export const mainEndpoints = {
  getMaincomponentsBySearch(customerId: EntityId, searchText?: string) {
    return `${mainComponentBaseURL(customerId)}/mainComponentsBySearch?search=${searchText ?? ''}`
  },
  getEquipmentsWithLocalityFilter(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/equipments-with-locality-filter`
  },
  getMainComponents(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}`
  },
  getEquipment(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/equipment`
  },
  getEquipmentTypes(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/equipment-types`
  },
  getMainComponentsForLocality(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/primitive-type-maincomponents-for-locality`
  },
  getMainComponentByIdWithAllData(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/with-all-data`
  },
  getMainComponentsList(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}`
  },
  deleteMainComponent(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}`
  },
  getOutgoingMainComponents(customerId: EntityId, localityId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/localities/${localityId}/outgoing`
  },
  getAdditionalEquipments(customerId: EntityId, localityId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/localities/${localityId}/get-additional-equipment-for-locality`
  },
  getActiveEquipmentForLocality(customerId: EntityId, localityId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/localities/${localityId}/active-equipment-for-locality`
  },
  addMainComponentToLocality(customerId: EntityId, positionId: EntityId | undefined) {
    return `${mainComponentBaseURL(customerId)}/positions/${positionId}`
  },
  createMainComponent(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}`
  },
  getMainComponentsByComponentType(customerId: EntityId, componentTypeId, searchText?: string) {
    return `${mainComponentBaseURL(customerId)}/componentTypes/${componentTypeId}?search=${searchText}`
  },
  getByMainComponentId(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}`
  },
  getMainComponentsByIds(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/get-by-ids`
  },
  updateMainComponent(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}`
  },
  bulkUpdateMainComponents(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/bulk-update`
  },
  deleteMainComponentById(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/discard`
  },
  getMainComponentStatusCount(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/mainComponentStatusCount`
  },
  getDiscardedMainComponents(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/discardedMainComponents`
  },
  exportToExcel(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/export`
  },
  changeCage(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/change-cage`
  },
  validateMaxCountForCage(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/validate-max-count-for-cage`
  },
  sendToServiceStation(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/send-to-service-station`
  },
  sendToStorage(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/send-to-storage`
  },
  sendToOutgoing(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/send-to-locality`
  },
  getDeviationCounts(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/get-deviation-counts`
  },
  releaseMainComponent(customerId) {
    return `${mainComponentBaseURL(customerId)}/release-main-component`
  },
  bulkReleaseMainComponents(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/release-main-components/bulk`
  },
  bulkSendToLocality(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/send-to-locality/bulk`
  },
  bulkSendToServiceStation(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/send-to-service-station/bulk`
  },
  bulkSendToStorage(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/send-to-storage/bulk`
  },
  bringBackMainComponent(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/bring-back-main-component`
  },
  getMaintenancePerformHistory(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/get-maintenance-perform-history`
  },
  getMainComponentROVVideos(customerId: EntityId, id: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${id}/rov-videos`
  },
  getMainComponentsBySearchBaseUrl(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/mainComponentsBySearch`
  },
  getMaincomponentsBySearchWithFilters(customerId: EntityId, searchText?: string, fieldFilters?: MainComponentFilters) {
    const params = new URLSearchParams()

    if (searchText) {
      params.append('search', searchText)
    }

    if (fieldFilters) {
      Object.entries(fieldFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'where' && Array.isArray(value)) {
            // Handle where array
            const valueSet = value as string[]
            valueSet.forEach(whereValue => {
              if (whereValue && whereValue.trim()) {
                params.append('where', whereValue.trim())
              }
            })
          } else if (key === 'status' && Array.isArray(value)) {
            // Handle status array
            const valueSet = value as number[]
            valueSet.forEach(statusValue => {
              params.append('status', statusValue.toString())
            })
          } else if (Array.isArray(value)) {
            // Handle other arrays
            value.forEach(item => {
              if (item !== null && item !== undefined) {
                params.append(key, item.toString())
              }
            })
          } else if (typeof value === 'string' && value.trim()) {
            params.append(key, value.trim())
          } else if (typeof value === 'number') {
            params.append(key, value.toString())
          }
        }
      })
    }

    return `${mainComponentBaseURL(customerId)}/mainComponentsBySearch?${params.toString()}`
  },
  deleteROVCategory(customerId: EntityId) {
    return `${mainComponentBaseURL(customerId)}`
  },
  changeOwner(customerId: EntityId, equipmentId: EntityId) {
    return `${mainComponentBaseURL(customerId)}/${equipmentId}/change-owner`
  },
}

export const invalidateMainComponentCache = {
  getEquipment(customerId?: EntityId, queryParams?: EquipmentRequestDto) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_EQUIPMENT(customerId, { ...queryParams }))
  },
  getEquipmentTypes(customerId?: EntityId, queryParams?: EquipmentRequestDto) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_EQUIPMENT_TYPES(customerId, { ...queryParams }))
  },
  getMainComponents(customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId, positionId?: EntityId) {
    queryClient.invalidateQueries(
      mainComponentQueryKeys.GET_MAIN_COMPONENTS(customerId, { localityId, mooringId, positionId }),
    )
  },
  getOutgoingMainComponents(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getOutgoingMainComponents(customerId, localityId))
  },
  getMainComponentsList(customerId: EntityId) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_MAIN_COMPONENTS_LIST(customerId))
  },
  getMainComponentById(customerId: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_MAIN_COMPONENT_BY_ID(customerId, id))
  },
  getMainComponentByIdAdmin(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getByMainComponentId(customerId, id))
  },
  getMainComponentStatusCount(customerId: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getMainComponentStatusCount(customerId))
  },
  getMainComponentsByComponentType(customerId: EntityId, componentTypeId: EntityId, search?: string) {
    queryClient.invalidateQueries(
      mainComponentQueryKeys.GET_MAIN_COMPONENTS_BY_COMPONENT_TYPE(customerId, componentTypeId, search),
    )
  },
  getDiscardedMainComponents(customerId: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getDiscardedMainComponents(customerId))
  },
  getDeviationCounts(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getDeviationCounts(customerId, id))
  },
  invalidateEquipment(customerId?: EntityId, queryParams?: EquipmentRequestDto) {
    invalidateMainComponentCache.getEquipment(customerId, queryParams)
    invalidateMainComponentCache.getEquipmentTypes(customerId, queryParams)
  },
  getActiveEquipmentForLocality(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(mainEndpoints.getActiveEquipmentForLocality(customerId, localityId))
  },
  getMaintenancePerformHistory(customerId?: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_MAINTENANCE_PERFORM_HISTORY(customerId, id))
  },
  getMaincomponentsBySearch(customerId, searchText?: string, queryParams?: object) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_BY_SEARCH(customerId, searchText, queryParams))
  },
  getMainComponentROVVideos(customerId: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_MAIN_COMPONENT_ROV_VIDEOS(customerId, id))
  },
  getMaincomponentsBySearchWithFilters(customerId, searchText?: string, fieldFilters?: MainComponentFilters) {
    queryClient.invalidateQueries(
      mainComponentQueryKeys.GET_BY_SEARCH_WITH_FILTERS(customerId, searchText, fieldFilters),
    )
  },
  getMainComponentsByIds(customerId: EntityId, ids?: EntityId[]) {
    queryClient.invalidateQueries(mainComponentQueryKeys.GET_MAIN_COMPONENTS_BY_IDS(customerId, ids))
  },

  /**
   * Common invalidations after any bulk action (release, send to storage, change location, etc.).
   * Accepts selected equipments (with id and optional typeId); ids and types are derived inside.
   */
  invalidateAfterBulkAction(
    customerId: EntityId,
    selectedEquipments: Array<{ id: EntityId; typeId?: EntityId; type?: { id?: EntityId } }>,
  ) {
    const ids = selectedEquipments.map(e => e.id)
    const typeIds = Array.from(
      new Set(selectedEquipments.map(e => e.typeId ?? e.type?.id).filter(Boolean)),
    ) as EntityId[]
    invalidateMainComponentCache.getEquipment(customerId)
    invalidateMainComponentCache.getMaincomponentsBySearch(customerId.toString())
    invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId.toString())
    invalidateMainComponentCache.getMainComponentsByIds(customerId, ids)
    typeIds.forEach(typeId => {
      invalidateMainComponentCache.getEquipmentTypes(customerId, { mainComponentTypeId: typeId })
      invalidateMainComponentCache.getMainComponentsByComponentType(customerId, typeId)
    })
    invalidationActivityLogCache.getCageActivityLogs()
  },

  /**
   * Invalidations after bulk release from cage. Includes common bulk action invalidations plus
   * per-released-item: main component by id, position deviations, locality, equipment types.
   */
  invalidateAfterBulkRelease(
    customerId: EntityId,
    releasedItems: Array<{
      id: EntityId
      selectedLocalityId?: EntityId
      positionId?: EntityId
      typeId?: EntityId
    }>,
  ) {
    invalidateMainComponentCache.invalidateAfterBulkAction(customerId, releasedItems)
    releasedItems.forEach(e => {
      invalidateMainComponentCache.getMainComponentById(customerId, e.id)
      if (e.selectedLocalityId && e.positionId) {
        invalidatePositionCache.getCagePositionDeviations(customerId, e.selectedLocalityId, e.positionId)
        invalidateLocalityCache.getLocality(customerId, e.selectedLocalityId)
        invalidateMainComponentCache.getEquipmentTypes(customerId, { mainComponentTypeId: e.typeId })
      }
    })
  },
}

export const useGetMainComponents = (
  customerId: EntityId,
  queryParams: MainComponentsRequestDto,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<IPaginatedResult<IMainComponent>>(
    mainEndpoints.getMainComponents(customerId),
    {
      ...queryParams,
      page,
      pageSize,
    },
    {
      queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENTS(customerId, queryParams),
    },
  )

export const useGetEquipment = (customerId: EntityId, queryParams: EquipmentRequestDto) =>
  useGet<Array<IMainComponent>>(
    mainEndpoints.getEquipment(customerId),
    {
      ...queryParams,
    },
    { queryKey: mainComponentQueryKeys.GET_EQUIPMENT(customerId, queryParams) },
  )

export const useGetEquipmentTypes = (customerId: EntityId, queryParams: EquipmentRequestDto) =>
  useGet<Array<IMainComponentType>>(
    mainEndpoints.getEquipmentTypes(customerId),
    {
      ...queryParams,
    },
    { queryKey: mainComponentQueryKeys.GET_EQUIPMENT_TYPES(customerId, queryParams) },
  )

export const useGetMainComponentsForLocality = (customerId: EntityId, queryParams: { localityId: EntityId }) =>
  useGet<Array<IMainComponent>>(mainEndpoints.getMainComponentsForLocality(customerId), {
    ...queryParams,
  })

export const useReleaseMainComponent = (customerId: EntityId) =>
  usePut<boolean, Entity>(mainEndpoints.releaseMainComponent(customerId))

export interface IBulkReleaseMainComponentsRequest {
  items: Array<{ id: number; localityId: number; formSchemaReports?: IFormSchemaReport[] }>
}

export const useBulkReleaseMainComponents = (customerId: EntityId) =>
  usePut<unknown, IBulkReleaseMainComponentsRequest>(mainEndpoints.bulkReleaseMainComponents(customerId))

export interface IBulkSendToLocalityItem {
  id: number
  localityId: number
  formSchemaReports?: IFormSchemaReport[]
}

export interface IBulkSendToLocalityRequest {
  items: IBulkSendToLocalityItem[]
  selectedLocalityId: number
  selectedPositionId?: number | null
  installationDate?: string | Date | null
  certificate?: {
    certificateNumber?: string
    certificateDate?: Date | string
    validUntil?: Date | string
    validPeriod?: number
    certificateDocumentType?: number
    fileId?: number
    supplierId?: number
    mainComponentId?: number
  } | null
  isForceAddedToLocality?: boolean
}

export const useBulkSendToLocality = (customerId: EntityId) =>
  usePut<unknown, IBulkSendToLocalityRequest>(mainEndpoints.bulkSendToLocality(customerId))

export interface IBulkSendToServiceStationItem {
  id: number
  localityId: number
  formSchemaReports?: IFormSchemaReport[]
}

export interface IBulkSendToServiceStationRequest {
  items: IBulkSendToServiceStationItem[]
  serviceStationId: number
}

export const useBulkSendToServiceStation = (customerId: EntityId) =>
  usePut<unknown, IBulkSendToServiceStationRequest>(mainEndpoints.bulkSendToServiceStation(customerId))

export interface IBulkSendToStorageItem {
  id: number
  localityId: number
  formSchemaReports?: IFormSchemaReport[]
}

export interface IBulkSendToStorageRequest {
  items: IBulkSendToStorageItem[]
  storageId: number
  hasPendingService?: boolean | null
}

export const useBulkSendToStorage = (customerId: EntityId) =>
  usePut<unknown, IBulkSendToStorageRequest>(mainEndpoints.bulkSendToStorage(customerId))

export const useBringBackMainComponent = (customerId: EntityId) =>
  usePut<boolean, object>(mainEndpoints.bringBackMainComponent(customerId))

export const useGetMainComponentById = (
  customerId: EntityId,
  id: EntityId,
  options?: { includeServiceStation?: boolean; includeStorage?: boolean; localityId?: EntityId },
) =>
  useGet<IMainComponent>(mainEndpoints.getMainComponentByIdWithAllData(customerId, id), options, {
    queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENT_BY_ID(customerId, id, options),
  })

export const useGetMainComponentsList = (
  customerId: EntityId,
  positionId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<IPaginatedResult<IMainComponent>>(
    mainEndpoints.getMainComponentsList(customerId),
    {
      positionId,
      page,
      pageSize,
    },
    { queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENTS_LIST(customerId, positionId) },
  )

export const useGetOutgoingMainComponents = (customerId: EntityId, localityId: EntityId) =>
  useGet<any>(mainEndpoints.getOutgoingMainComponents(customerId, localityId))

export const useGetAdditionalEquipments = (customerId: EntityId, localityId: EntityId) =>
  useGet<any>(mainEndpoints.getAdditionalEquipments(customerId, localityId))

export const useGetActiveEquipmentForLocality = (
  customerId: EntityId,
  localityId: EntityId,
  includedMainComponentIds?: string,
) =>
  useGet<any>(mainEndpoints.getActiveEquipmentForLocality(customerId, localityId), {
    includedMainComponentIds: includedMainComponentIds,
  })

export const useChangeCageInMainComponent = (customerId: EntityId, mainComponentId: EntityId) =>
  usePut<boolean, any>(mainEndpoints.changeCage(customerId, mainComponentId))

export interface IValidateMaxCountForCageRequest {
  positionId: EntityId
  mainComponentIds: EntityId[]
}

export interface IValidateMaxCountForCageResult {
  isExceeded: boolean
  exceededMainComponentIds: number[]
}

export const useValidateMaxCountForCage = (customerId: EntityId) =>
  usePost<IValidateMaxCountForCageResult, IValidateMaxCountForCageRequest>(
    mainEndpoints.validateMaxCountForCage(customerId),
  )

export const useGetMainComponentsByMainComponentId = (customerId: EntityId, id: EntityId) =>
  useGet<any>(mainEndpoints.getByMainComponentId(customerId, id))

export const useGetMainComponentsByIds = (customerId: EntityId, ids: EntityId[]) =>
  useGet<IMainComponent[]>(
    mainEndpoints.getMainComponentsByIds(customerId),
    { ids },
    {
      queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENTS_BY_IDS(customerId, ids),
      enabled: !!customerId && Array.isArray(ids) && ids.length > 0,
    },
  )

export const useGetMainComponentsByComponentType = (customerId: EntityId, componentTypeId, searchText?: string) =>
  useGet<any>(
    mainEndpoints.getMainComponentsByComponentType(customerId, componentTypeId, searchText),
    {},
    {
      queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENTS_BY_COMPONENT_TYPE(customerId, componentTypeId, searchText),
    },
  )

export const useCreateMainComponent = (customerId: EntityId) =>
  usePost<IMainComponent, IMainComponent>(mainEndpoints.createMainComponent(customerId))

export const useUpdateMainComponent = (customerId: EntityId, id: EntityId) =>
  usePut<IMainComponent, IMainComponentUpdate>(mainEndpoints.updateMainComponent(customerId, id))

export interface IBulkUpdateMainComponentsRequest {
  mainComponentIds: number[]
  orderNumber?: string
  reference?: string
  description?: string
  solidity?: number
  lifetime?: number
  circumference?: number
  supplierId?: number
  productionYear?: string
  productionMonth?: number
  maintenanceTemplateIds?: number[]
}

export const useBulkUpdateMainComponents = (customerId: EntityId) =>
  usePut<unknown, IBulkUpdateMainComponentsRequest>(mainEndpoints.bulkUpdateMainComponents(customerId))

export const useUpdateMainComponentToOutgoing = (customerId: EntityId, id: EntityId) =>
  usePut<boolean, object>(mainEndpoints.sendToOutgoing(customerId, id))

export const useDiscardEquipmentById = (customerId: EntityId) =>
  usePut<IMainComponent, IDiscardEquipmentDto>(mainEndpoints.deleteMainComponentById(customerId))

export const useGetMainComponentStatusCount = (customerId: EntityId) =>
  useGet<{ result: IMainComponentStatusCount }>(mainEndpoints.getMainComponentStatusCount(customerId))

export const useGetDiscardedMainComponents = (customerId: EntityId, search?: string) =>
  useGet<IPaginatedResult<IMainComponent>>(mainEndpoints.getDiscardedMainComponents(customerId), { search })

export const useDeleteMainComponent = (customerId: EntityId) =>
  useDelete<IMainComponent>(mainEndpoints.deleteMainComponent(customerId))

export const useAssignMainComponentToServiceStation = (customerId: EntityId, id: EntityId) =>
  usePut<IMainComponentSendSchema, IMainComponentSendSchema>(mainEndpoints.sendToServiceStation(customerId, id))

export const useSendToStorage = (customerId: EntityId, id: EntityId) =>
  usePut<IMainComponent, IMainComponent>(mainEndpoints.sendToStorage(customerId, id))

export const useAddMainComponentToLocality = (customerId: EntityId, positionId: EntityId | undefined) => {
  return usePut<boolean, object>(mainEndpoints.addMainComponentToLocality(customerId, positionId))
}

export const useGetExportToExcel = (customerId: EntityId) =>
  usePost<any, IExportRequest>(mainEndpoints.exportToExcel(customerId), {}, { responseType: 'blob' })

export const useGetMainComponentDeviationCount = (customerId: EntityId, id: EntityId) =>
  useGet<IDeviationCounts>(mainEndpoints.getDeviationCounts(customerId, id), {}, { enabled: id ? true : false })

export const useGetMaintenancePerformHistory = (customerId: EntityId, id: EntityId) =>
  useGet<any>(
    mainEndpoints.getMaintenancePerformHistory(customerId, id),
    {},
    { queryKey: mainComponentQueryKeys.GET_MAINTENANCE_PERFORM_HISTORY(customerId, id) },
  )

export const useGetMainComponentBySearch = (
  customerId: EntityId,
  searchText?: string,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<any>(
    mainEndpoints.getMaincomponentsBySearch(customerId, searchText),
    {
      page,
      pageSize,
    },
    {
      queryKey: mainComponentQueryKeys.GET_BY_SEARCH(customerId, searchText, { page, pageSize }),
    },
  )

export const useGetEquipmentsWithLocalityFilter = (
  customerId: EntityId,
  queryParams: { search?: string; localityId?: EntityId; excludeId?: EntityId; page?: number; pageSize?: number },
) =>
  useGet<IPaginatedResult<IEquipmentWithLocalityFilterDto>>(
    mainEndpoints.getEquipmentsWithLocalityFilter(customerId),
    queryParams,
    { queryKey: mainComponentQueryKeys.GET_EQUIPMENTS_WITH_LOCALITY_FILTER(customerId, queryParams) },
  )

export const useGetMainComponentROVVideos = (customerId: EntityId, id: EntityId) =>
  useGet<IROVVideosWithSharedCategories>(
    mainEndpoints.getMainComponentROVVideos(customerId, id),
    {},
    {
      queryKey: mainComponentQueryKeys.GET_MAIN_COMPONENT_ROV_VIDEOS(customerId, id),
      refetchInterval: rovVideosRefetchInterval,
    },
  )

export const useGetMainComponentBySearchWithFilters = (
  customerId: EntityId,
  searchText?: string,
  fieldFilters?: MainComponentFilters,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  enabled = true,
) =>
  useGet<any>(
    mainEndpoints.getMaincomponentsBySearchWithFilters(customerId, searchText, fieldFilters),
    {
      page,
      pageSize,
    },
    {
      queryKey: mainComponentQueryKeys.GET_BY_SEARCH_WITH_FILTERS(customerId, searchText, fieldFilters, page, pageSize),
      enabled,
    },
  )

export const useDeleteMainComponentROVCategory = (customerId: EntityId) =>
  useDelete<any>(mainEndpoints.deleteROVCategory(customerId))

export const useChangeMainComponentOwner = (customerId: EntityId, equipmentId: EntityId) =>
  usePut<any, object>(mainEndpoints.changeOwner(customerId, equipmentId))
